import * as React from "react";
import { twMerge } from "tailwind-merge";

export interface NumberInputBoxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  placeholder?: string;
  className?: string;
  wrapperClassName?: string;
  disabled?: boolean;
  required?: boolean;
  showIcon?: boolean;
  onIconClick?: () => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  iconMinimal?: boolean;
  min?: number;
  max?: number;
  step?: number;
} 

const NumberInputBox = React.forwardRef<HTMLInputElement, NumberInputBoxProps>(
  (
    {
      label,
      placeholder = "입력",
      className = "",
      wrapperClassName = "",
      disabled = false,
      required = false,
      showIcon = false,
      onIconClick,
      onChange,
      iconMinimal = false,
      readOnly,
      min,
      max,
      step = 1,
      value,
      onBlur,
      ...props
    },
    ref
  ) => {
    const hasLabel = label !== undefined;
    const inputRef = React.useRef<HTMLInputElement>(null);
    const combinedRef = React.useCallback(
      (node: HTMLInputElement) => {
        inputRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );

    const clampValue = (rawValue: number) => {
      let result = rawValue;
      if (min !== undefined) {
        result = Math.max(result, min);
      }
      if (max !== undefined) {
        result = Math.min(result, max);
      }
      return result;
    };

    const dispatchInputEvent = (newValue: number) => {
      if (!inputRef.current) return;
      inputRef.current.value = String(newValue);
      inputRef.current.dispatchEvent(new Event("input", { bubbles: true }));
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.value === "") {
        onChange?.(event);
        return;
      }

      const numericValue = Number(event.target.value);
      if (Number.isNaN(numericValue)) {
        event.target.value = "";
        onChange?.(event);
        return;
      }

      const clamped = clampValue(numericValue);
      if (clamped !== numericValue) {
        event.target.value = String(clamped);
      }

      onChange?.(event);
    };

    const handleStepUp = () => {
      if (disabled || readOnly || !inputRef.current) return;
      const currentValue = Number(inputRef.current.value) || 0;
      const newValue = clampValue(currentValue + (step || 1));
      onChange?.({ target: { value: String(newValue) } } as unknown as React.ChangeEvent<HTMLInputElement>);
    };

    const handleStepDown = () => {
      if (disabled || readOnly || !inputRef.current) return;
      const currentValue = Number(inputRef.current.value) || 0;
      const newValue = clampValue(currentValue - (step || 1));
      onChange?.({ target: { value: String(newValue) } } as unknown as React.ChangeEvent<HTMLInputElement>);
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      if (disabled || readOnly) {
        onBlur?.(event);
        return;
      }

      const currentValue = Number(event.target.value);
      if (!Number.isNaN(currentValue)) {
        const clamped = clampValue(currentValue);
        if (clamped !== currentValue) {
          dispatchInputEvent(clamped);
        }
      } else if (min !== undefined) {
        dispatchInputEvent(min);
      } else if (max !== undefined) {
        dispatchInputEvent(max);
      }

      onBlur?.(event);
    };

    return (
      <div className={twMerge("relative", wrapperClassName)}>
        {/* Label - 속성 존재 시에만 영역 생성 */}
        {hasLabel && (
          <div className="h-6 mb-1">
            <label className="block text-base font-normal text-[#414651]">
              {required && <span className="text-red-500 mr-1">*</span>}
              {label}
            </label>
          </div>
        )}

        {/* Input Box */}
        <div className="relative">
          <style>{`
            input[type="number"]::-webkit-inner-spin-button,
            input[type="number"]::-webkit-outer-spin-button {
              -webkit-appearance: none;
              margin: 0;
            }
            input[type="number"] {
              -moz-appearance: textfield;
            }
          `}</style>
          <input
            min={min}
            max={max}
            step={step}
            type="number"
            onChange={handleInputChange}
            onBlur={handleBlur}
            ref={combinedRef}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            readOnly={readOnly}
            value={value}
            className={twMerge(
              "w-full border border-[#CCCCCC] rounded-md px-2 py-2",
              "focus:outline-none focus:ring-2 focus:ring-[#55b938] focus:border-[#55b938]",
              disabled ? "bg-[#F6F6F6] cursor-not-allowed text-gray-500" : "bg-white cursor-text",
              readOnly ? "bg-[#F5F5F5] cursor-default" : "",
              "text-[#333333] placeholder:text-[#AAAAAA]",
              "pr-10",
              className
            )}
            {...props}
          />

          {/* 위/아래 화살표 버튼 - SelectBox와 동일한 스타일 */}
          <div className="absolute right-1 top-[50%] -translate-y-1/2 flex flex-col gap-0 h-auto w-[20px]">
            <button
              type="button"
              onClick={handleStepUp}
              disabled={disabled || readOnly}
              className={`flex items-center justify-center p-0 m-0 h-[20px] ${disabled || readOnly ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:opacity-70"}`}
              tabIndex={-1}
            >
              <img
                src="/icons/headerArrowBlack.svg"
                alt="증가"
                className="h-[20px] w-[20px] rotate-180 mb-[-5px]"
              />
            </button>
            <button
              type="button"
              onClick={handleStepDown}
              disabled={disabled || readOnly}
              className={`flex items-center justify-center p-0 m-0 h-[20px] ${disabled || readOnly ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:opacity-70"}`}
              tabIndex={-1}
            >
              <img
                src="/icons/headerArrowBlack.svg"
                alt="감소"
                className="h-[20px] w-[20px] mt-[-5px]"
              />
            </button>
          </div>
        </div>
      </div>
    );
  }
);

NumberInputBox.displayName = "NumberInputBox";

export { NumberInputBox };
